import React, { useState, useEffect, useRef } from "react";
import {
  FileText,
  UploadCloud,
  Loader2,
  CheckCircle2,
  Play,
  RotateCcw,
  Save,
  LayoutDashboard,
  Check,
  X,
  Trash2,
  Link2,
  Plus,
} from "lucide-react";
import Step from "../components/Common/Step"; // Ensure this path is correct
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import axios from "axios";

export default function RequirementMapper({ darkMode }) {
  // Enhanced Color Palette based on theme
  const borderColor = darkMode ? "border-gray-800" : "border-gray-200";
  const cardBg = darkMode ? "bg-[#11131a]" : "bg-white";
  const subCardBg = darkMode ? "bg-[#1a1d27]" : "bg-gray-50";
  const tableHeaderBg = darkMode ? "bg-[#1a1d27]" : "bg-gray-50";
  const tableHoverBg = darkMode ? "hover:bg-gray-800/50" : "hover:bg-gray-50";
  const textMuted = darkMode ? "text-gray-400" : "text-gray-500";
  const textMain = darkMode ? "text-gray-100" : "text-gray-900";

  // Fetch Project ID from Redux store matching BugReport structure
  const { selectedProject } = useSelector((state) => state.auth);

  // State Management
  const [currentStep, setCurrentStep] = useState(1);
  const [docFile, setDocFile] = useState(null);
  const [imgFiles, setImgFiles] = useState([]);
  const [imgPreviews, setImgPreviews] = useState([]);
  const [jobId, setJobId] = useState(null);
  const [testCases, setTestCases] = useState([]);
  const [jiraModalOpen, setJiraModalOpen] = useState(false);
  const [jiraStories, setJiraStories] = useState([]);
  const [selectedJiraKeys, setSelectedJiraKeys] = useState([]);
  const [importedJiraStories, setImportedJiraStories] = useState([]);
  const [jiraLoading, setJiraLoading] = useState(false);
  const [baseUrl, setBaseUrl] = useState("");
  const [sprintTag, setSprintTag] = useState("");
  const [roleCredentials, setRoleCredentials] = useState([]);
  const [navigationNotes, setNavigationNotes] = useState("");
  const [customTestVariables, setCustomTestVariables] = useState([
    { key: "", value: "" },
  ]);

  const docInputRef = useRef(null);
  const imgInputRef = useRef(null);

  const openJiraStoryPicker = async () => {
    if (!selectedProject?.project_id) {
      toast.error("Please select a project first.");
      return;
    }
    setJiraModalOpen(true);
    setJiraLoading(true);
    try {
      const response = await fetch(
        `https://api.testoraglobal.in/api/jira/${selectedProject.project_id}/stories`,
      );
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Unable to fetch Jira stories.");
      setJiraStories(data.stories || []);
      setSelectedJiraKeys([]);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setJiraLoading(false);
    }
  };

  const importSelectedJiraStories = () => {
    const selectedStories = jiraStories.filter((story) =>
      selectedJiraKeys.includes(story.jira_key),
    );
    setImportedJiraStories((current) => {
      const existingKeys = new Set(current.map((story) => story.jira_key));
      return [
        ...current,
        ...selectedStories.filter((story) => !existingKeys.has(story.jira_key)),
      ];
    });
    setJiraModalOpen(false);
    toast.success(
      `${selectedStories.length} Jira ${selectedStories.length === 1 ? "story" : "stories"} selected for analysis.`,
    );
  };

  // Generate Image Preview safely
  useEffect(() => {
    const objectUrls = imgFiles.map((file) => URL.createObjectURL(file));
    setImgPreviews(objectUrls);
    return () => objectUrls.forEach((url) => URL.revokeObjectURL(url));
  }, [imgFiles]);

  const canStartAnalysis =
    imgFiles.length > 0 && (Boolean(docFile) || importedJiraStories.length > 0);

  const handleProcess = async () => {
    if (!selectedProject || !selectedProject.project_id) {
      toast.error("Please select a project first.");
      return;
    }

    if (!docFile && importedJiraStories.length === 0) {
      toast.error(
        "Please provide either a requirement document or import a Jira story",
      );
      return;
    }

    if (!docFile && !baseUrl.trim()) {
      toast.error(
        "Enter the Application Base URL when analyzing Jira stories without a requirement document.",
      );
      return;
    }

    if (imgFiles.length === 0) {
      toast.error("Please upload at least one UI design.");
      return;
    }

    const providedCredentials = roleCredentials.filter(
      (credential) =>
        credential.role.trim() ||
        credential.email.trim() ||
        credential.password,
    );
    if (
      providedCredentials.some(
        (credential) =>
          !credential.role.trim() ||
          !credential.email.trim() ||
          !credential.password,
      )
    ) {
      toast.error(
        "Complete every Role, Email, and Password field or remove the empty credential row.",
      );
      return;
    }

    const customVariables = {};
    for (const variable of customTestVariables) {
      const key = variable.key.trim();
      if (!key) continue;
      if (!variable.value.trim()) {
        toast.error(
          `Enter a concrete value for the custom test variable "${key}".`,
        );
        return;
      }
      if (Object.prototype.hasOwnProperty.call(customVariables, key)) {
        toast.error(
          `The custom test variable "${key}" is listed more than once.`,
        );
        return;
      }
      customVariables[key] = variable.value;
    }

    const formData = new FormData();
    if (docFile) formData.append("document", docFile);
    imgFiles.forEach((file) => formData.append("images", file));
    formData.append("project_id", selectedProject.project_id);
    formData.append("jira_stories", JSON.stringify(importedJiraStories));
    if (sprintTag.trim()) formData.append("sprint_tag", sprintTag.trim());
    if (baseUrl.trim()) formData.append("base_url", baseUrl.trim());
    if (providedCredentials.length)
      formData.append("role_credentials", JSON.stringify(providedCredentials));
    if (navigationNotes.trim())
      formData.append("navigation_notes", navigationNotes.trim());
    if (Object.keys(customVariables).length)
      formData.append("custom_test_variables", JSON.stringify(customVariables));

    try {
      setCurrentStep(2); // Show Loading Screen
      const { data } = await axios.post(
        "https://api.testoraglobal.in/api/ai/reqmap/upload",
        formData,
      );
      setJobId(data.job_id);
      toast.success("Assets uploaded, starting AI analysis...");
    } catch (error) {
      console.error("Upload failed", error);
      toast.error(
        error.response?.data?.error || error.message || "Upload failed.",
      );
      setCurrentStep(1);
    }
  };

  useEffect(() => {
    let interval;
    if (jobId && currentStep === 2) {
      interval = setInterval(async () => {
        try {
          const { data } = await axios.get(
            `https://api.testoraglobal.in/api/ai/reqmap/status/${jobId}`,
          );

          if (data.status === "COMPLETED") {
            setTestCases(data.result);
            setCurrentStep(3);
            toast.success("AI Analysis completed successfully!");
            clearInterval(interval);
          } else if (data.status === "FAILED") {
            toast.error(`Analysis failed: ${data.error}`);
            setCurrentStep(1);
            clearInterval(interval);
          }
        } catch (error) {
          if (
            error.response?.status === 401 ||
            error.response?.status === 403
          ) {
            clearInterval(interval);
          }
          console.error("Polling error", error);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [jobId, currentStep]);

  // --- LOGIC: Discard Everything & Clear Execution Context ---
  const handleDiscard = () => {
    // Clear Assets
    setDocFile(null);
    setImgFiles([]);
    setImgPreviews([]);
    setTestCases([]);
    setImportedJiraStories([]);
    setJobId(null);

    // Clear Execution Context Fields
    setBaseUrl("");
    setSprintTag("");
    setRoleCredentials([]);
    setNavigationNotes("");
    setCustomTestVariables([{ key: "", value: "" }]);

    // Reset Inputs and Step
    if (docInputRef.current) docInputRef.current.value = "";
    if (imgInputRef.current) imgInputRef.current.value = "";
    setCurrentStep(1);
  };

  // --- LOGIC: Save Single Test Case ---
  const handleSaveSingle = async (tc, index) => {
    if (!selectedProject || !selectedProject.project_id) {
      toast.error("Please select a project before saving.");
      return;
    }

    const payload = {
      project_id: selectedProject.project_id,
      test_id: tc.test_id || `TC-${String(index + 1).padStart(3, "0")}`,
      module: tc.module || "General",
      "feature/submodule": tc["feature/submodule"] || tc.feature || "",
      category:
        tc.category || tc.Category || tc["ui-ux/functionality"] || "Functional",
      type: tc.type || "Positive",
      "test-case-title":
        tc["test-case-title"] || tc.title || "Generated Test Case",
      description: tc.description || "",
      preconditions: tc.preconditions || "",
      test_steps: tc.test_steps || [],
      expected_result: tc.expected_result || "",
      base_url: tc.base_url || "",
      jira_key: tc.jira_key || "",
      jira_url: tc.jira_url || "",
      sprint_tag: sprintTag.trim(),
    };

    try {
      await axios.post("https://api.testoraglobal.in/api/testcases/create", payload);
      toast.success(`Saved ${payload.test_id}`);
      // Remove from list
      const updatedList = testCases.filter((_, i) => i !== index);
      setTestCases(updatedList);
      if (updatedList.length === 0) handleDiscard(); // Go back & reset if empty
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Server error while saving.",
      );
    }
  };

  // --- LOGIC: Save All Test Cases ---
  const handleSaveAll = async () => {
    if (!selectedProject || !selectedProject.project_id) {
      toast.error("Please select a project before saving.");
      return;
    }

    const loadingToast = toast.loading("Saving all test cases...");

    try {
      const promises = testCases.map((tc, index) => {
        const payload = {
          project_id: selectedProject.project_id,
          test_id: tc.test_id || `TC-${String(index + 1).padStart(3, "0")}`,
          module: tc.module || "General",
          "feature/submodule": tc["feature/submodule"] || tc.feature || "",
          category:
            tc.category ||
            tc.Category ||
            tc["ui-ux/functionality"] ||
            "Functional",
          type: tc.type || "Positive",
          "test-case-title":
            tc["test-case-title"] || tc.title || "Generated Test Case",
          description: tc.description || "",
          preconditions: tc.preconditions || "",
          test_steps: tc.test_steps || [],
          expected_result: tc.expected_result || "",
          base_url: tc.base_url || "",
          jira_key: tc.jira_key || "",
          jira_url: tc.jira_url || "",
          sprint_tag: sprintTag.trim(),
        };

        return axios.post(
          "https://api.testoraglobal.in/api/testcases/create",
          payload,
        );
      });

      await Promise.all(promises);
      toast.success("All test cases saved successfully!", { id: loadingToast });
      handleDiscard(); // Clear assets and execution context, reset to Step 1
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to save some test cases.",
        { id: loadingToast },
      );
    }
  };

  // --- LOGIC: Reject Test Case ---
  const handleReject = (index) => {
    const updatedList = testCases.filter((_, i) => i !== index);
    setTestCases(updatedList);
    if (updatedList.length === 0) handleDiscard();
  };

  return (
    <div
      className={`px-4 md:px-8 pb-12 max-w-[90rem] mx-auto animate-in fade-in duration-500 ${textMain}`}
    >
      {/* HEADER */}
      <div className="mb-10 mt-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-4xl font-extrabold tracking-tight mb-2">
              Requirement Mapper
            </h2>
            <p className={`${textMuted} text-lg`}>
              Transform your BRDs and UI mocks into comprehensive test suites
              instantly.
            </p>
          </div>
          {/* Show the active project */}
          <div
            className={`px-4 py-2 rounded-xl border ${borderColor} ${cardBg} flex items-center gap-3 shadow-sm`}
          >
            <LayoutDashboard size={16} className="text-purple-500" />
            <div className="flex flex-col">
              <span className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">
                Active Project
              </span>
              <span className="text-sm font-semibold">
                {selectedProject?.project_name || "None Selected"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* STEPPER WIZARD */}
      <div
        className={`p-4 rounded-2xl border ${cardBg} ${borderColor} mb-10 shadow-sm flex items-center justify-center`}
      >
        <div className="flex items-center w-full max-w-4xl">
          <Step Number="1" Label="Upload Assets" active={currentStep >= 1} />
          <div
            className={`flex-1 h-px mx-4 ${darkMode ? "bg-gray-800" : "bg-gray-200"}`}
          ></div>
          <Step Number="2" Label="AI Analysis" active={currentStep >= 2} />
          <div
            className={`flex-1 h-px mx-4 ${darkMode ? "bg-gray-800" : "bg-gray-200"}`}
          ></div>
          <Step Number="3" Label="Review & Save" active={currentStep === 3} />
        </div>
      </div>

      {/* STEP 1: UPLOAD SECTION */}
      {currentStep === 1 && (
        <div
          className={`p-8 rounded-3xl border ${cardBg} ${borderColor} shadow-lg`}
        >
          <details
            className={`mb-8 rounded-xl border ${borderColor} ${subCardBg}`}
          >
            <summary className="cursor-pointer px-5 py-4 font-semibold">
              Execution Context (Optional)
              <span className={`ml-2 text-xs font-normal ${textMuted}`}>
                Add URL, role credentials, navigation guidance, and test data.
              </span>
            </summary>
            <div className="space-y-5 border-t border-inherit px-5 py-5">
              <label className="block">
                <span className="mb-1 block text-sm font-medium">
                  Application Base URL
                </span>
                <input
                  type="url"
                  value={baseUrl}
                  onChange={(event) => setBaseUrl(event.target.value)}
                  placeholder="https://app.example.com"
                  className={`w-full rounded-lg border ${borderColor} ${cardBg} px-3 py-2 outline-none focus:border-purple-500`}
                />
              </label>

              <label className="block">
                <span className="mb-1 block text-sm font-medium">
                  Target Sprint / Release Version (Optional)
                </span>
                <input
                  type="text"
                  value={sprintTag}
                  onChange={(event) => setSprintTag(event.target.value)}
                  placeholder="e.g. Sprint 12 or Release 2.4"
                  className={`w-full rounded-lg border ${borderColor} ${cardBg} px-3 py-2 outline-none focus:border-purple-500`}
                />
              </label>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium">Role Credentials</span>
                  <button
                    type="button"
                    onClick={() =>
                      setRoleCredentials((current) => [
                        ...current,
                        { role: "", email: "", password: "" },
                      ])
                    }
                    className="flex items-center gap-1 text-sm font-semibold text-purple-500"
                  >
                    <Plus size={16} /> Add role
                  </button>
                </div>
                <div className="space-y-3">
                  {roleCredentials.map((credential, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1.5fr_1.5fr_auto]"
                    >
                      <input
                        aria-label={`Role name ${index + 1}`}
                        value={credential.role}
                        onChange={(event) =>
                          setRoleCredentials((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, role: event.target.value }
                                : item,
                            ),
                          )
                        }
                        placeholder="Role name"
                        className={`min-w-0 rounded-lg border ${borderColor} ${cardBg} px-3 py-2`}
                      />
                      <input
                        aria-label={`Role email ${index + 1}`}
                        type="email"
                        value={credential.email}
                        onChange={(event) =>
                          setRoleCredentials((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, email: event.target.value }
                                : item,
                            ),
                          )
                        }
                        placeholder="Email"
                        className={`min-w-0 rounded-lg border ${borderColor} ${cardBg} px-3 py-2`}
                      />
                      <input
                        aria-label={`Role password ${index + 1}`}
                        type="password"
                        value={credential.password}
                        onChange={(event) =>
                          setRoleCredentials((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, password: event.target.value }
                                : item,
                            ),
                          )
                        }
                        placeholder="Password (stored privately)"
                        className={`min-w-0 rounded-lg border ${borderColor} ${cardBg} px-3 py-2`}
                      />
                      <button
                        type="button"
                        aria-label={`Remove role credential ${index + 1}`}
                        onClick={() =>
                          setRoleCredentials((current) =>
                            current.filter(
                              (_, itemIndex) => itemIndex !== index,
                            ),
                          )
                        }
                        className="rounded-lg px-2 text-red-500 hover:bg-red-500/10"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
                <p className={`mt-2 text-xs ${textMuted}`}>
                  Credential passwords are stored with the project and are not
                  sent to Gemini.
                </p>
              </div>

              <label className="block">
                <span className="mb-1 block text-sm font-medium">
                  Navigation Note
                </span>
                <textarea
                  value={navigationNotes}
                  onChange={(event) => setNavigationNotes(event.target.value)}
                  placeholder="After login, click the left sidebar."
                  rows={3}
                  className={`w-full rounded-lg border ${borderColor} ${cardBg} px-3 py-2 outline-none focus:border-purple-500`}
                />
              </label>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium">
                    Custom Test Variables
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setCustomTestVariables((current) => [
                        ...current,
                        { key: "", value: "" },
                      ])
                    }
                    className="flex items-center gap-1 text-sm font-semibold text-purple-500"
                  >
                    <Plus size={16} /> Add variable
                  </button>
                </div>
                <div className="space-y-2">
                  {customTestVariables.map((variable, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_2fr_auto]"
                    >
                      <input
                        aria-label={`Test variable name ${index + 1}`}
                        value={variable.key}
                        onChange={(event) =>
                          setCustomTestVariables((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, key: event.target.value }
                                : item,
                            ),
                          )
                        }
                        placeholder="Variable name (e.g. company)"
                        className={`min-w-0 rounded-lg border ${borderColor} ${cardBg} px-3 py-2`}
                      />
                      <input
                        aria-label={`Test variable value ${index + 1}`}
                        value={variable.value}
                        onChange={(event) =>
                          setCustomTestVariables((current) =>
                            current.map((item, itemIndex) =>
                              itemIndex === index
                                ? { ...item, value: event.target.value }
                                : item,
                            ),
                          )
                        }
                        placeholder="Concrete value (e.g. Acme_Corp)"
                        className={`min-w-0 rounded-lg border ${borderColor} ${cardBg} px-3 py-2`}
                      />
                      <button
                        type="button"
                        aria-label={`Remove test variable ${index + 1}`}
                        onClick={() =>
                          setCustomTestVariables((current) =>
                            current.filter(
                              (_, itemIndex) => itemIndex !== index,
                            ),
                          )
                        }
                        className="rounded-lg px-2 text-red-500 hover:bg-red-500/10"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </details>

          <div className="flex justify-between items-center mb-8 pb-6 border-b border-gray-200 dark:border-gray-800">
            <div>
              <h3 className="text-2xl font-bold mb-2">
                Upload Requirement Documents
              </h3>
              <p className={textMuted}>
                Upload a BRD/FSD or import Jira stories (a document is optional
                when Jira stories are imported), plus up to five UI designs.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={openJiraStoryPicker}
                className={`flex items-center gap-2 rounded-xl border ${borderColor} px-5 py-3.5 font-semibold transition-colors ${tableHoverBg}`}
              >
                <Link2 size={18} /> Import from Jira
              </button>
              <button
                onClick={handleProcess}
                disabled={!canStartAnalysis}
                className="
                group
                relative
                overflow-hidden
                px-8
                py-3.5
                rounded-xl
                font-semibold
                text-white
                bg-gradient-to-r
                from-purple-600
                to-indigo-600
                hover:from-purple-700
                hover:to-indigo-700
                transition-all
                duration-300
                shadow-lg
                hover:shadow-purple-500/30
                hover:scale-[1.02]
                flex
                items-center
                gap-2
              "
              >
                <Play
                  size={18}
                  className="group-hover:translate-x-0.5 transition-transform"
                />
                Start AI Analysis
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* DOCUMENT UPLOAD */}
            <div>
              <input
                type="file"
                ref={docInputRef}
                className="hidden"
                accept=".pdf,.doc,.docx"
                onChange={(e) => setDocFile(e.target.files[0])}
              />

              <div
                onClick={() => docInputRef.current.click()}
                className={`
            relative
            border
            border-dashed
            rounded-2xl
            p-8
            cursor-pointer
            transition-all
            duration-300
            hover:border-purple-500
            hover:shadow-lg
            ${subCardBg}
            ${docFile ? "border-purple-500" : borderColor}
          `}
              >
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="w-16 h-16 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4">
                    <FileText size={32} className="text-purple-500" />
                  </div>

                  <h4 className="font-semibold text-lg mb-1">
                    Upload Requirement Documents
                  </h4>

                  <p className={`${textMuted} text-sm`}>
                    BRD, FSD, FRD files (PDF, DOC, DOCX)
                  </p>
                </div>
              </div>

              {docFile && (
                <div
                  className={`mt-3 p-3 rounded-xl border ${borderColor} flex items-center justify-between`}
                >
                  <div
                    className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => {
                      const url = URL.createObjectURL(docFile);
                      window.open(url, "_blank");
                    }}
                    title="Click to view file"
                  >
                    <CheckCircle2 size={18} className="text-green-500" />
                    <div>
                      <p className="text-sm font-medium text-blue-500 hover:underline">
                        {docFile.name}
                      </p>
                      <p className={`text-xs ${textMuted}`}>
                        {(docFile.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDocFile(null);
                    }}
                    className="p-1 rounded-full hover:bg-red-500/10 text-red-500 transition-colors"
                    title="Remove file"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>

            {/* UI DESIGN UPLOAD */}
            <div>
              <input
                type="file"
                ref={imgInputRef}
                className="hidden"
                accept="image/png,image/jpeg,image/jpg"
                multiple
                onChange={(e) => {
                  const newFiles = Array.from(e.target.files || []);

                  setImgFiles((prevFiles) => {
                    const totalFiles = [...prevFiles, ...newFiles];

                    if (totalFiles.length > 5) {
                      toast.error("You can upload a maximum of 5 UI designs.");
                      return totalFiles.slice(0, 5);
                    }
                    return totalFiles;
                  });

                  // Clear the input value so the same file can be uploaded again if needed
                  if (e.target) {
                    e.target.value = "";
                  }
                }}
              />

              <div
                onClick={() => imgInputRef.current.click()}
                className={`
            relative
            border
            border-dashed
            rounded-2xl
            p-8
            cursor-pointer
            transition-all
            duration-300
            hover:border-purple-500
            hover:shadow-lg
            ${subCardBg}
                    ${imgFiles.length > 0 ? "border-purple-500" : borderColor}
          `}
              >
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="w-16 h-16 rounded-xl bg-purple-500/10 flex items-center justify-center mb-4">
                    <UploadCloud size={32} className="text-purple-500" />
                  </div>

                  <h4 className="font-semibold text-lg mb-1">
                    Upload UI Designs
                  </h4>

                  <p className={`${textMuted} text-sm`}>
                    Up to 5 PNG or JPG UI designs
                  </p>
                </div>
              </div>

              {imgFiles.length > 0 && (
                <div className={`mt-3 p-3 rounded-xl border ${borderColor}`}>
                  {imgFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${file.lastModified}-${index}`}
                      className="flex items-center justify-between mb-3 last:mb-0"
                    >
                      <div
                        className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() =>
                          window.open(imgPreviews[index], "_blank")
                        }
                        title="Click to view image"
                      >
                        <CheckCircle2 size={18} className="text-green-500" />
                        <div>
                          <p className="text-sm font-medium text-blue-500 hover:underline">
                            {file.name}
                          </p>
                          <p className={`text-xs ${textMuted}`}>
                            {(file.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setImgFiles((files) =>
                            files.filter((_, fileIndex) => fileIndex !== index),
                          );
                        }}
                        className="p-1 rounded-full hover:bg-red-500/10 text-red-500 transition-colors"
                        title="Remove image"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {importedJiraStories.length > 0 && (
            <div
              className={`mt-8 overflow-hidden rounded-xl border ${borderColor}`}
            >
              <div
                className={`flex items-center justify-between border-b ${borderColor} px-5 py-4`}
              >
                <div>
                  <h4 className="font-semibold">Imported Jira Requirements</h4>
                  <p className={`mt-1 text-xs ${textMuted}`}>
                    {importedJiraStories.length} stories will be included in AI
                    analysis.
                  </p>
                </div>
                <button
                  onClick={() => setImportedJiraStories([])}
                  className="text-xs font-semibold text-red-500 hover:text-red-600"
                >
                  Clear all
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className={tableHeaderBg}>
                    <tr>
                      <th className="px-5 py-3 font-semibold">Jira Key</th>
                      <th className="px-5 py-3 font-semibold">Story</th>
                      <th className="px-5 py-3 font-semibold">Description</th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {importedJiraStories.map((story) => (
                      <tr
                        key={story.jira_key}
                        className={`border-t ${borderColor}`}
                      >
                        <td className="px-5 py-3 font-mono text-xs font-semibold">
                          {story.jira_key}
                        </td>
                        <td className="px-5 py-3 font-medium">
                          {story.summary}
                        </td>
                        <td className="max-w-md px-5 py-3">
                          <textarea
                            aria-label={`Description for ${story.jira_key}`}
                            value={story.description || ""}
                            onChange={(event) =>
                              setImportedJiraStories((current) =>
                                current.map((item) =>
                                  item.jira_key === story.jira_key
                                    ? {
                                        ...item,
                                        description: event.target.value,
                                      }
                                    : item,
                                ),
                              )
                            }
                            placeholder="Add requirement details to guide test generation"
                            rows={2}
                            className={`w-full min-w-48 rounded-lg border ${borderColor} ${cardBg} px-3 py-2 text-sm ${textMain}`}
                          />
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            aria-label={`Remove ${story.jira_key}`}
                            onClick={() =>
                              setImportedJiraStories((current) =>
                                current.filter(
                                  (item) => item.jira_key !== story.jira_key,
                                ),
                              )
                            }
                            className="rounded p-1 text-gray-500 hover:bg-red-500/10 hover:text-red-500"
                          >
                            <X size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* STEP 2: AI ANALYSIS */}
      {currentStep === 2 && (
        <div
          className={`
      min-h-[500px]
      rounded-3xl
      border
      ${cardBg}
      ${borderColor}
      flex
      flex-col
      items-center
      justify-center
      p-10
    `}
        >
          <div className="relative mb-8">
            <div className="absolute inset-0 rounded-full border-4 border-purple-500/20"></div>

            <div className="w-24 h-24 rounded-full border-4 border-transparent border-t-purple-500 border-r-purple-400 animate-spin"></div>

            <div className="absolute inset-3 rounded-full border-4 border-transparent border-t-indigo-400 animate-spin [animation-duration:1.5s]"></div>
          </div>

          <h3 className="text-3xl font-bold mb-3">AI Analysis in Progress</h3>

          <p className={`${textMuted} text-center max-w-lg mb-8`}>
            Mapping requirements, understanding UI flows, identifying business
            rules and generating optimized test cases.
          </p>

          <div className="w-full max-w-md">
            <div className="h-2 rounded-full bg-gray-700 overflow-hidden">
              <div className="h-full w-1/3 bg-gradient-to-r from-purple-500 to-indigo-500 animate-pulse"></div>
            </div>
          </div>

          <div className="mt-6 text-sm text-purple-500 animate-pulse">
            Processing uploaded assets...
          </div>
        </div>
      )}

      {/* STEP 3: REVIEW TABLE */}
      {currentStep === 3 && (
        <div
          className={`rounded-3xl border ${cardBg} ${borderColor} shadow-xl flex flex-col`}
        >
          {/* Toolbar */}
          <div
            className={`p-6 border-b ${borderColor} flex flex-col sm:flex-row justify-between items-center gap-4`}
          >
            <div>
              <h3 className="text-2xl font-bold flex items-center gap-2">
                <CheckCircle2 className="text-green-500" />
                Generated Test Cases
              </h3>
              <p className={`${textMuted} text-sm mt-1`}>
                {testCases.length} scenarios pending review.
              </p>
            </div>

            {/* UPDATED BUTTONS */}
            <div className="flex gap-3">
              <button
                onClick={handleDiscard}
                className={`flex items-center gap-2 border ${borderColor} ${tableHoverBg} px-4 py-2.5 rounded-lg font-medium transition-colors text-red-500`}
              >
                <Trash2 size={18} /> Discard
              </button>
              <button
                onClick={handleProcess}
                className={`flex items-center gap-2 border ${borderColor} ${tableHoverBg} px-4 py-2.5 rounded-lg font-medium transition-colors text-blue-500`}
              >
                <RotateCcw size={18} /> Reanalyse
              </button>
              <button
                onClick={handleSaveAll}
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors"
              >
                <Save size={18} /> Approve All
              </button>
            </div>
          </div>

          {/* Scroll hint bar */}
          <div
            className={`flex items-center gap-2 px-6 py-2 text-xs ${textMuted} border-b ${borderColor} ${tableHeaderBg}`}
          >
            <span>&#8592; Scroll horizontally to see all columns &#8594;</span>
          </div>

          {/* THE SCROLLABLE DIV — this is the key part */}
          <div
            style={{
              overflowX: "auto",
              overflowY: "visible",
              width: "100%",
              borderRadius: "0 0 1.5rem 1.5rem",
            }}
          >
            <table
              style={{
                minWidth: "1500px",
                width: "100%",
                borderCollapse: "collapse",
                tableLayout: "fixed",
                textAlign: "left",
              }}
            >
              {/* Column widths */}
              <colgroup>
                <col style={{ width: "80px" }} />
                <col style={{ width: "110px" }} />
                <col style={{ width: "110px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "110px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "200px" }} />
                <col style={{ width: "190px" }} />
                <col style={{ width: "160px" }} />
                <col style={{ width: "230px" }} />
                <col style={{ width: "180px" }} />
                <col style={{ width: "80px" }} />
              </colgroup>

              {/* Header */}
              <thead>
                <tr
                  style={{
                    borderBottom: darkMode
                      ? "1px solid #1f2937"
                      : "1px solid #e5e7eb",
                    background: darkMode ? "#1a1d27" : "#f9fafb",
                  }}
                >
                  {[
                    "ID",
                    "Jira Story",
                    "Module",
                    "Feature",
                    "Category",
                    "Type",
                    "Test Name",
                    "Description",
                    "Preconditions",
                    "Test Steps",
                    "Expected Result",
                    "Action",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "12px 14px",
                        fontSize: "11px",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: darkMode ? "#6b7280" : "#9ca3af",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textAlign: h === "Action" ? "center" : "left",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Rows */}
              <tbody>
                {testCases.map((tc, index) => (
                  <tr
                    key={index}
                    style={{
                      borderBottom: darkMode
                        ? "1px solid #1f2937"
                        : "1px solid #f3f4f6",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = darkMode
                        ? "rgba(55,65,81,0.3)"
                        : "#f9fafb")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    {/* ID */}
                    <td
                      style={{
                        padding: "14px",
                        fontSize: "12px",
                        fontFamily: "monospace",
                        verticalAlign: "top",
                      }}
                    >
                      {tc.test_id || `TC-${String(index + 1).padStart(3, "0")}`}
                    </td>

                    <td
                      style={{
                        padding: "14px",
                        fontSize: "12px",
                        verticalAlign: "top",
                      }}
                    >
                      {tc.jira_key && tc.jira_url ? (
                        <a
                          href={tc.jira_url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-semibold text-blue-500 hover:underline"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {tc.jira_key}
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>

                    {/* Module */}
                    <td
                      style={{
                        padding: "14px",
                        fontSize: "13px",
                        fontWeight: 600,
                        verticalAlign: "top",
                      }}
                    >
                      {tc.module || "General"}
                    </td>

                    {/* Feature */}
                    <td
                      style={{
                        padding: "14px",
                        fontSize: "13px",
                        verticalAlign: "top",
                        wordBreak: "break-word",
                      }}
                    >
                      {tc["feature/submodule"] || tc.feature || "N/A"}
                    </td>

                    {/* Category */}
                    <td
                      style={{
                        padding: "14px",
                        fontSize: "13px",
                        verticalAlign: "top",
                        wordBreak: "break-word",
                      }}
                    >
                      {tc.category ||
                        tc.Category ||
                        tc["ui-ux/functionality"] ||
                        "N/A"}
                    </td>

                    {/* Type */}
                    <td style={{ padding: "14px", verticalAlign: "top" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "2px 10px",
                          borderRadius: "9999px",
                          fontSize: "11px",
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          background: tc.type
                            ?.toLowerCase()
                            .includes("positive")
                            ? "rgba(34,197,94,0.1)"
                            : tc.type?.toLowerCase().includes("negative")
                              ? "rgba(239,68,68,0.1)"
                              : "rgba(234,179,8,0.1)",
                          color: tc.type?.toLowerCase().includes("positive")
                            ? "#16a34a"
                            : tc.type?.toLowerCase().includes("negative")
                              ? "#dc2626"
                              : "#ca8a04",
                        }}
                      >
                        {tc.type || "N/A"}
                      </span>
                    </td>

                    {/* Test Name */}
                    <td
                      style={{
                        padding: "14px",
                        fontSize: "13px",
                        fontWeight: 600,
                        verticalAlign: "top",
                        wordBreak: "break-word",
                      }}
                    >
                      {tc["test-case-title"] || "N/A"}
                    </td>

                    {/* Description */}
                    <td
                      style={{
                        padding: "14px",
                        fontSize: "13px",
                        verticalAlign: "top",
                        wordBreak: "break-word",
                        lineHeight: 1.6,
                      }}
                    >
                      {tc.description || "N/A"}
                    </td>

                    {/* Preconditions */}
                    <td
                      style={{
                        padding: "14px",
                        fontSize: "13px",
                        verticalAlign: "top",
                        wordBreak: "break-word",
                        lineHeight: 1.6,
                      }}
                    >
                      {tc.preconditions || "N/A"}
                    </td>

                    {/* Test Steps */}
                    <td style={{ padding: "14px", verticalAlign: "top" }}>
                      <ol
                        style={{
                          margin: 0,
                          paddingLeft: "16px",
                          listStyle: "decimal",
                        }}
                      >
                        {Array.isArray(tc.test_steps) ? (
                          tc.test_steps.map((step, i) => (
                            <li
                              key={i}
                              style={{
                                fontSize: "12px",
                                lineHeight: 1.6,
                                marginBottom: "2px",
                                color: darkMode ? "#9ca3af" : "#6b7280",
                              }}
                            >
                              {step}
                            </li>
                          ))
                        ) : (
                          <li
                            style={{
                              fontSize: "12px",
                              color: darkMode ? "#9ca3af" : "#6b7280",
                            }}
                          >
                            {tc.test_steps || "N/A"}
                          </li>
                        )}
                      </ol>
                    </td>

                    {/* Expected Result */}
                    <td
                      style={{
                        padding: "14px",
                        fontSize: "13px",
                        fontWeight: 500,
                        verticalAlign: "top",
                        wordBreak: "break-word",
                        lineHeight: 1.6,
                      }}
                    >
                      {tc.expected_result || "N/A"}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: "14px", verticalAlign: "top" }}>
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          onClick={() => handleSaveSingle(tc, index)}
                          title="Approve & Save"
                          style={{
                            padding: "6px",
                            borderRadius: "8px",
                            border: "1px solid rgba(34,197,94,0.3)",
                            background: "rgba(34,197,94,0.08)",
                            color: "#16a34a",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Check size={15} />
                        </button>
                        <button
                          onClick={() => handleReject(index)}
                          title="Reject"
                          style={{
                            padding: "6px",
                            borderRadius: "8px",
                            border: "1px solid rgba(239,68,68,0.3)",
                            background: "rgba(239,68,68,0.08)",
                            color: "#dc2626",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <X size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {testCases.length === 0 && (
              <div
                style={{
                  padding: "48px",
                  textAlign: "center",
                  color: "#6b7280",
                }}
              >
                No test case generated. Check backend logs.
              </div>
            )}
          </div>
        </div>
      )}

      {jiraModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3 sm:p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setJiraModalOpen(false);
            }
          }}
        >
          <section
            className={`flex h-auto max-h-[75vh] min-h-0 w-full max-w-2xl flex-col overflow-hidden rounded-xl border ${borderColor} ${cardBg} shadow-2xl`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="jira-modal-title"
          >
            {/* Fixed Header */}
            <div
              className={`flex shrink-0 items-center justify-between border-b ${borderColor} px-4 py-3 sm:px-5`}
            >
              <div className="min-w-0">
                <h3
                  id="jira-modal-title"
                  className="text-base font-bold sm:text-lg"
                >
                  Import Jira Stories
                </h3>

                <p className={`mt-1 text-xs sm:text-sm ${textMuted}`}>
                  Select open stories, epics, or tasks to include in this
                  analysis.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setJiraModalOpen(false)}
                aria-label="Close dialog"
                className="ml-2 shrink-0 rounded-lg p-2 text-gray-500 transition hover:bg-gray-800/10"
              >
                <X size={18} />
              </button>
            </div>

            {/* Independently Scrollable Story List */}
            <div
              className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain p-3 sm:p-4"
              style={{
                maxHeight: "calc(75vh - 125px)",
                WebkitOverflowScrolling: "touch",
              }}
            >
              {jiraLoading ? (
                <div
                  className={`flex items-center justify-center gap-2 py-10 ${textMuted}`}
                >
                  <Loader2 className="animate-spin" size={18} />
                  <span>Loading Jira stories...</span>
                </div>
              ) : jiraStories.length === 0 ? (
                <p className={`py-10 text-center text-sm ${textMuted}`}>
                  No open Jira stories found.
                </p>
              ) : (
                <div className="min-w-0 divide-y divide-gray-200 dark:divide-gray-800">
                  {jiraStories.map((story) => {
                    const isSelected = selectedJiraKeys.includes(
                      story.jira_key,
                    );

                    return (
                      <label
                        key={story.jira_key}
                        className={`flex min-w-0 cursor-pointer gap-3 rounded-lg px-3 py-3 transition ${tableHoverBg}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(event) => {
                            const checked = event.target.checked;

                            setSelectedJiraKeys((current) =>
                              checked
                                ? current.includes(story.jira_key)
                                  ? current
                                  : [...current, story.jira_key]
                                : current.filter(
                                    (key) => key !== story.jira_key,
                                  ),
                            );
                          }}
                          className="mt-1 h-4 w-4 shrink-0 accent-purple-600"
                        />

                        <span className="min-w-0 flex-1">

                          
                          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <span className="shrink-0 mr-3 font-mono text-sm font-semibold text-purple-500">
                              {story.jira_key}
                            </span>

                            <span className="min-w-0 break-words text-sm font-semibold">
                              {story.summary}
                            </span>
                          </span>

                          <span
                            className={`mt-1 block whitespace-pre-wrap break-words text-sm [overflow-wrap:anywhere] ${textMuted}`}
                          >
                            {/* {story.description || "No description"} */}
                          </span>



                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Fixed Footer */}
            <div
              className={`flex shrink-0 items-center justify-between gap-3 border-t ${borderColor} px-4 py-3 sm:px-5`}
            >
              <span className={`text-xs sm:text-sm ${textMuted}`}>
                {selectedJiraKeys.length} selected
              </span>

              <div className="flex shrink-0 gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => setJiraModalOpen(false)}
                  className={`rounded-lg border ${borderColor} px-3 py-2 text-sm transition hover:bg-gray-800/10 sm:px-4`}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={importSelectedJiraStories}
                  disabled={jiraLoading || selectedJiraKeys.length === 0}
                  className="rounded-lg bg-purple-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4"
                >
                  Import
                </button>
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
